import React, { useCallback, useState } from 'react';
import { Typography, Grid, FormControl, Divider, CircularProgress } from "@mui/material";
import { Box } from "@mui/system";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import NCRFormHeading from '../NCRFormHeading';
import NCRFormLabel from '../NCRFormLabel';
import { useDropzone } from "react-dropzone";
import toast from 'react-hot-toast';
import NCRMultilineTextfields from '../NCRMultilineTextfields';
import { processFileForUpload } from "../../../utils/imageCompressor";

export default function PreventiveActionReview({ content, onChange }) {
  const [isCompressing, setIsCompressing] = useState(false);

  const theme = createTheme({
    components: {
      MuiSelect: {
        styleOverrides: {
          select: {
            fontFamily: 'Lato !important',
            fontSize: "14px",
            fontWeight: 400,
            color: "rgba(84, 84, 84, 1)",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&::placeholder": {
              fontFamily: 'Lato !important',
              fontSize: "14px",
              fontWeight: 400,
              color: "rgba(84, 84, 84, 1)",
              opacity: 1,
            },
          },
        },
      },
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onChange({ [name]: value });
  };

  const handleFileChange = (file) => {
    if (file) {
      onChange({
        preventive_action_review_verification_evidence: file,
      });
    }
  };

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      toast.error("Only PDF, JPG, JPEG, and PNG files are allowed.");
      return;
    }
    const file = acceptedFiles[0];
    if (!file) return;

    const fileSizeMB = file.size / (1024 * 1024);

    // If file is over 10MB, reject completely
    if (fileSizeMB > 10) {
      toast.error("File size should not exceed 10MB!");
      return;
    }

    // If file is between 2MB and 10MB, try to compress (only for images)
    if (fileSizeMB > 2) {
      if (!file.type.startsWith('image/')) {
        toast.error("PDF files must be under 2MB. Only images can be compressed.");
        return;
      }

      setIsCompressing(true);
      try {
        const { file: processedFile, wasCompressed } = await processFileForUpload(file, 2, 10);
        if (wasCompressed) {
          toast.success(`Image compressed from ${fileSizeMB.toFixed(1)}MB to ${(processedFile.size / (1024 * 1024)).toFixed(1)}MB`);
        }
        handleFileChange(processedFile);
      } catch (error) {
        toast.error(error.message || "Failed to compress image");
      } finally {
        setIsCompressing(false);
      }
      return;
    }

    // File is under 2MB, use as is
    handleFileChange(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    multiple: false,
  });

  const handleFileDeleteIcon = () => {
    onChange({ preventive_action_review_verification_evidence: "" });
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <Box>
          <Box sx={{ p: 3 }}>
            <Box>
              <NCRFormHeading isRequired={true} tooltipData={
                [
                  { description: "This section focuses on reviewing the long-term preventive measures to ensure the issue does not recur." },
                ]
              }>
                Preventive Action Review
              </NCRFormHeading>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2} xs={12} sm={12}>
                  <Grid item xs={12} sm={4}>
                    <NCRFormLabel>
                      Has the Preventive Action been implemented?
                    </NCRFormLabel>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <FormControl>
                      <RadioGroup
                        row
                        name="preventive_action_review_implemented"
                        onChange={handleInputChange}
                        value={content.formData.preventive_action_review_implemented === null ? 'Yes' : content.formData.preventive_action_review_implemented}
                      >
                        <FormControlLabel disabled={content.inputDisable} value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel disabled={content.inputDisable} value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2} xs={12} sm={12}>
                  <Grid item xs={12} sm={4}>
                    <NCRFormLabel>
                      Is the Preventive Action sufficient to prevent recurrence?
                    </NCRFormLabel>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <FormControl>
                      <RadioGroup
                        row
                        name="preventive_action_review_sufficient_prevent_recurrence"
                        onChange={handleInputChange}
                        value={content.formData.preventive_action_review_sufficient_prevent_recurrence === null ? 'Yes' : content.formData.preventive_action_review_sufficient_prevent_recurrence}
                      >
                        <FormControlLabel disabled={content.inputDisable} value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel disabled={content.inputDisable} value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Grid item xs={4}>
                <NCRFormLabel sx={{ mb: 1 }}>
                  Detailed Feedback:
                </NCRFormLabel>
              </Grid>
              <Grid item xs={8}>
                <NCRMultilineTextfields
                  name="preventive_action_review_feedback"
                  value={content.formData.preventive_action_review_feedback || ""}
                  onChange={handleInputChange}
                  disabled={content.inputDisable}
                  error={!!content?.errors?.preventive_action_review_feedback}
                  placeholder={!content.inputDisable ? 'Enter your detailed feedback' : 'No data entered'}
                />
              </Grid>
            </Box>
            <Box sx={{ mt: 3 }}>
              <NCRFormHeading>Verification Evidence:</NCRFormHeading>
              {content.inputDisable ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <img
                    src="/images/pdf-icon.png"
                    alt="NC Pro"
                    style={{ maxWidth: "100%", height: "auto" }}
                  />
                  <NCRFormLabel
                    sx={{
                      px: 1,
                      fontStyle: !content.formData.preventive_action_review_verification_evidence ? 'italic' : 'normal',
                      '&:hover': content.formData.preventive_action_review_verification_evidence ? { cursor: 'pointer' } : {},
                    }}
                    onClick={() => {
                      if (content.formData.preventive_action_review_verification_evidence) {
                        window.open(`${process.env.REACT_APP_BACKEND_APPLICATION_URL}/uploads/${content.formData.preventive_action_review_verification_evidence.filename}`, '_blank');
                      }
                    }}
                  >
                    {content.formData.preventive_action_review_verification_evidence?.originalname || 'File does not exist'}
                  </NCRFormLabel>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: "1px dashed rgba(0, 0, 0, 0.3)",
                      py: 1,
                      px: 4,
                      textAlign: "center",
                      cursor: isCompressing ? "wait" : "pointer",
                      borderRadius: "5px",
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      opacity: isCompressing ? 0.7 : 1,
                    }}
                  >
                    <Box sx={{ display: "none" }}>
                      <input {...getInputProps()} disabled={isCompressing} />
                    </Box>
                    {isCompressing ? (
                      <>
                        <CircularProgress size={24} sx={{ mb: 1 }} />
                        <Typography sx={{ fontFamily: 'Lato', fontSize: '14px', fontWeight: 400, color: 'rgba(84, 84, 84, 1)' }}>
                          Compressing image...
                        </Typography>
                      </>
                    ) : (
                      <>
                        <img
                          src="/images/upload-icon.png"
                          alt="NC Pro"
                          style={{ maxWidth: "100%", height: "auto" }}
                        />
                        <Typography
                          sx={{
                            fontFamily: 'Lato',
                            fontSize: '14px',
                            fontWeight: 400,
                          }}
                        >
                          <span style={{ textDecoration: 'underline', color: 'rgba(47, 70, 216, 1)' }}>Browse</span> or{" "}
                          <span style={{ color: 'rgba(84, 84, 84, 1)' }}>Drag & drop your files</span>
                        </Typography>
                      </>
                    )}
                  </Box>
                  {content.formData.preventive_action_review_verification_evidence && (
                    <Box sx={{ display: 'flex', alignItems: 'center', pt: 1 }}>
                      <img
                        src="/images/pdf-icon.png"
                        alt="NC Pro"
                        style={{ maxWidth: "100%", height: "auto" }}
                      />
                      <Typography sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 1)', p: 1 }}>
                        {
                          content.formData.preventive_action_review_verification_evidence?.originalname
                          || content.formData.preventive_action_review_verification_evidence?.name
                          || content.formData.preventive_action_review_verification_evidence?.filename
                        }
                      </Typography>
                      <img
                        src="/images/file-delete-icon.png"
                        alt="NC Pro"
                        onClick={handleFileDeleteIcon}
                        style={{ cursor: 'pointer', maxWidth: "100%", height: "auto" }}
                      />
                    </Box>
                  )}
                  <NCRFormLabel
                    sx={{ fontSize: '16px', fontWeight: 500, mt: 1 }}
                  >
                    Maximum file size: 10MB
                  </NCRFormLabel>
                </Box>
              )}
            </Box>
          </Box>
          {content.label === 'review' ? (
            <div style={{ paddingBottom: '10px' }} />
          ) : (
            <Divider sx={{ mt: 1.5, mb: 0.5 }} />
          )}
        </Box>
      </ThemeProvider>
    </>
  );
};
